import React, { memo, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useUpdateProjectAssignee, useUserGroupsAllUsers } from '../../../../services/user.service';
import { Select } from '../../../../components/Select';
import * as Styled from './style';
import { EditIcon } from '../../Funds/icons';
import { FiCheck, FiX } from 'react-icons/fi';
import { USER_ROLES } from '../../../../consts/userRoles';

export const ASSIGNEE_EDITING_STATES = {
  DEFAULT: 'default',
  SELECT: 'selecting'
} as const;

export type AssigneeEditingStateKey =
  (typeof ASSIGNEE_EDITING_STATES)[keyof typeof ASSIGNEE_EDITING_STATES];

interface AssigneeEditingState {
  id: string | undefined;
  state: AssigneeEditingStateKey;
}

interface ProjectUser {
  azure_object_id?: string;
  name?: string;
  [key: string]: unknown;
}

interface AssignedToColumnProps {
  project: {
    id: string;
    assigned_user_id?: string;
    assigned_user_name?: string;
    [key: string]: unknown;
  };
  currentUserState?: { role?: string };
  assigneeEditingState: AssigneeEditingState;
  setAssigneeEditingState: (state: AssigneeEditingState) => void;
}

const AssignedToColumn = ({
  project,
  currentUserState,
  assigneeEditingState,
  setAssigneeEditingState
}: AssignedToColumnProps) => {
  const { data: projectUsers, isLoading } = useUserGroupsAllUsers({
    customKey: 'project_list'
  }) as {
    data?: ProjectUser[];
    isLoading: boolean;
  };
  const updateProjectAssignee = useUpdateProjectAssignee();
  const [selectValue, setSelectValue] = useState<ProjectUser | null>(null);
  const [assignedUserName, setAssignedUserName] = useState<string>(
    project?.assigned_user_name || 'Not assigned'
  );

  const setAssigneeEditingStateToDefault = () => {
    setAssigneeEditingState({
      id: undefined,
      state: ASSIGNEE_EDITING_STATES.DEFAULT
    });
  };

  const handleApproveButtonClick = () => {
    if (!selectValue?.azure_object_id) {
      setAssigneeEditingStateToDefault();
      return;
    }

    const { azure_object_id, name } = selectValue;
    updateProjectAssignee.mutate(
      {
        workflowId: project.id,
        assignedUserId: azure_object_id,
        data: {
          assigned_user_id: azure_object_id
        }
      },
      {
        onSuccess: () => {
          toast.success(`${name} has been assigned to the project`);
        },
        onError: (err: any) => {
          console.log(`AssignedToColumn::handleSelectProjectAssignee`, err);
          const errorMessage = err?.data?.detail || 'Something went wrong while assigning a user';
          toast.error(errorMessage);
        }
      }
    );
    setAssignedUserName(name || '');
    setAssigneeEditingStateToDefault();
  };

  const handleSelectProjectAssignee = (selectedValue: ProjectUser) => {
    setSelectValue(selectedValue);
  };

  const handleRejectButtonClick = () => {
    setAssigneeEditingStateToDefault();
  };

  const handleEditingButtonClick = () => {
    setAssigneeEditingState({
      id: project.id,
      state: ASSIGNEE_EDITING_STATES.SELECT
    });
  };

  const renderEditingState = () => {
    const isCurrentProjectBeingAssigned =
      assigneeEditingState.state === ASSIGNEE_EDITING_STATES.SELECT &&
      assigneeEditingState.id === project.id;

    if (isCurrentProjectBeingAssigned) {
      return (
        <Styled.AssigneeSelectStateWrapper>
          <Select
            id="select-project-assign"
            value={selectValue}
            isSearchable
            options={projectUsers}
            getOptionValue={(opt: ProjectUser) => opt?.azure_object_id}
            getOptionLabel={(opt: ProjectUser) => opt?.name}
            onChange={handleSelectProjectAssignee}
            isLoading={isLoading}
            menuPortalTarget={document.body}
            styles={{
              container: (base: any) => ({ ...base, minWidth: '200px' }),
              menu: (base: any) => ({ ...base, width: '200px' })
            }}
          />
          <Styled.EditIconButton aria-label="Approve" onClick={handleApproveButtonClick}>
            <FiCheck className="approve" size={18} color="#CED4DA" />
          </Styled.EditIconButton>
          <Styled.EditIconButton aria-label="Cancel" onClick={handleRejectButtonClick}>
            <FiX className="reject" size={18} color="#CED4DA" />
          </Styled.EditIconButton>
        </Styled.AssigneeSelectStateWrapper>
      );
    }

    return (
      <Styled.AssigneeDefaultStateWrapper>
        <span>{assignedUserName}</span>
        <Styled.EditIconButton aria-label="Edit assignee" onClick={handleEditingButtonClick}>
          <EditIcon size={18} color="#CED4DA" />
        </Styled.EditIconButton>
      </Styled.AssigneeDefaultStateWrapper>
    );
  };

  useEffect(() => {
    if (!projectUsers?.length || !project?.assigned_user_id) return;

    const currentAssignedUser = projectUsers?.find(
      user => user?.azure_object_id === project.assigned_user_id
    );

    setSelectValue(currentAssignedUser || null);
  }, [project, projectUsers]);

  if (currentUserState?.role === USER_ROLES.ADMIN) {
    return <Styled.AssignedToColumnWrap>{renderEditingState()}</Styled.AssignedToColumnWrap>;
  }

  return <Styled.AssignedToColumnWrap>{assignedUserName}</Styled.AssignedToColumnWrap>;
};

export default memo(AssignedToColumn);
